import { useEffect, useState } from 'react';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useNewMeetversePostsIndicator } from '@hooks/useNewMeetversePostsIndicator';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { cn } from '@lib/utils';

interface NewPostsPillProps {
  /** Disable tap while another refresh path is already in flight (e.g. pull-to-refresh). */
  isBusy?: boolean;
}

export function NewPostsPill({ isBusy = false }: NewPostsPillProps) {
  const indicator = useNewMeetversePostsIndicator();
  const { hasNewContent, acknowledge } = indicator;
  // Fallbacks match the invariant the discriminated union now encodes at
  // compile time: no content → no poster, single-author framing.
  const latestPosterFirstName = indicator.hasNewContent ? indicator.latestPosterFirstName : null;
  const multipleAuthors = indicator.hasNewContent ? indicator.multipleAuthors : false;
  const { trackEvent } = useAmplitudeTracking();

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  // Render-time conditional setState — same pattern as PullRefreshIndicator.
  // This is the canonical alternative to setState-in-effect: React handles
  // the cascading render synchronously and there's no eslint warning.
  if (hasNewContent && !mounted) {
    setMounted(true);
  }
  if (!hasNewContent && visible) {
    setVisible(false);
  }

  // Defer the visible flip one frame so the entry transition actually
  // animates from opacity-0 / translate-y-(-2) instead of snapping in.
  // setState inside rAF avoids the set-state-in-effect rule.
  useEffect(() => {
    if (!mounted || !hasNewContent || visible) return undefined;
    const id = requestAnimationFrame(() => {
      setVisible(true);
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [mounted, hasNewContent, visible]);

  // Stuck-mount fallback. Race: hasNewContent flips false within the ~16ms
  // window between mount and rAF firing setVisible(true). The button stays
  // mounted at opacity-0 with pointer-events-auto + aria-live: focusable
  // and screen-reader-discoverable but invisible. No transitionend would
  // ever fire (opacity-0 to opacity-0 is a no-op), so onTransitionEnd can't
  // recover. Schedule a 250ms timeout (> transition-duration of 200ms) to
  // force-unmount. Cleanup cancels the timeout when transitionend fires
  // first during a normal exit, so this only effectively triggers in the
  // rare race path. Production observability: emit pill_stuck_mount_recovered
  // when the timeout actually fires so frequent triggers (a backend or SSE
  // flap) don't hide behind the now-fixed UX.
  useEffect(() => {
    if (!mounted || hasNewContent || visible) return undefined;
    const id = window.setTimeout(() => {
      setMounted(false);
      trackEvent(ANALYTICS_EVENTS.pill_stuck_mount_recovered, {});
    }, 250);
    return () => {
      window.clearTimeout(id);
    };
  }, [mounted, hasNewContent, visible, trackEvent]);

  if (!mounted) return null;

  // Evidence-based copy variant. The detector hook examines up to 3 of the
  // latest posts and reports whether they came from one or multiple distinct
  // authors. Names give warmth (a brand-defining property at Meetball), and
  // we use real evidence to choose singular vs plural rather than guessing.
  const copy = !latestPosterFirstName
    ? 'New posts'
    : multipleAuthors
      ? `${latestPosterFirstName} and others posted`
      : `${latestPosterFirstName} posted`;

  const handleClick = () => {
    if (isBusy) return;
    acknowledge();
  };

  return (
    // Fixed to the viewport — the pill stays visible regardless of scroll
    // position so users see "new content available" even when 10 pages deep.
    // pointer-events-none on the wrapper keeps the surrounding gap
    // non-interactive; only the button itself reacts to clicks.
    //
    // aria-live + role="status" live on the wrapper (non-interactive) so
    // screen-reader announcements fire on text content changes without the
    // accessibility tree treating the button as both interactive AND a live
    // region. The button itself is gated on `visible` for focus/clicks so
    // it's not focusable while transparent during entry/exit transitions.
    <div
      // 76px under the header clears Home's sticky tab bar (51) + its fade (24) + 1 for the
      // shadow. Re-measure if HomeTabBar padding or the fade height changes.
      className="fixed inset-x-0 top-[calc(var(--header-height)+76px)] z-10 flex justify-center pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={isBusy}
        aria-hidden={!visible}
        tabIndex={visible ? 0 : -1}
        className={cn(
          // Soft "indicator" treatment: pale yellow fill + warm yellow border.
          // Different semantic from primary CTAs — announces state, not
          // commands action. Deliberately not the brand `bg-mb-yellow`,
          // decided during iterative visual testing (see commit 31eb679e).
          'rounded-full border',
          'bg-mb-yellow-pale border-mb-gold-border hover:bg-mb-yellow-pale-hover',
          'text-black',
          'px-4 py-2 text-sm font-medium shadow-md',
          'transition-all duration-200 ease-out',
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2',
          // pointer-events gated on visible so an invisible button can't
          // be clicked through, even though the parent's pointer-events-none
          // already covers the gap. Belt-and-suspenders during transitions.
          visible ? 'pointer-events-auto' : 'pointer-events-none',
          'disabled:cursor-not-allowed disabled:opacity-60',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2 focus-visible:ring-offset-mb-bg',
        )}
        onTransitionEnd={() => {
          if (!visible) setMounted(false);
        }}
      >
        {copy}
      </button>
    </div>
  );
}
